import React, { type HTMLAttributeAnchorTarget, type PropsWithChildren } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type ReturnZepIcon } from '@/icons/ZepIcon';
import { cn } from '@/utils/tailwind.util';
export type SidebarMenu = {
  label: string;
  href: string | null;
  target: HTMLAttributeAnchorTarget | null;
  icon: ReturnZepIcon;
  activeIcon?: ReturnZepIcon;
  onClick: () => void;
  hasDivider?: boolean;
  rightSlot?: React.ReactNode;
  activeHrefs?: string[];
};
export const isSidebarMenuActive = (pathname: string, href: string | null, activeHrefs: string[] = []) => {
  const candidates = [href, ...activeHrefs].filter((value): value is string => Boolean(value));
  return candidates.some(candidate => pathname === candidate || pathname.startsWith(`${candidate}/`));
};
export function Sidebar(props: {
  menus: SidebarMenu[];
  footerMenus: SidebarMenu[];
}) {
  return <nav className="flex h-full w-full flex-col justify-between bg-default px-md pb-2xl pt-xl shadow-sm" data-sentry-component="Sidebar" data-sentry-source-file="Sidebar.tsx">
      <ul className="no-scrollbar flex min-h-0 w-full flex-1 flex-col gap-sm overflow-y-auto">
        {props.menus.map((item, index, all) => <React.Fragment key={item.label}>
            <Menu key={item.label} {...item} />
            {item.hasDivider && all.length !== index + 1 && <div className="h-px w-full shrink-0 border-t border-alternative" key={item.label + index} />}
          </React.Fragment>)}
      </ul>
      <div className="flex w-full flex-col gap-3xs">
        {props.footerMenus.map(item => <Menu key={item.label} {...item} />)}
      </div>
    </nav>;
}
const Menu = (item: SidebarMenu) => {
  const {
    pathname
  } = useRouter();
  const isActive = isSidebarMenuActive(pathname, item.href, item.activeHrefs);
  const Icon = isActive && item.activeIcon ? item.activeIcon : item.icon;
  const innerClassName = 'flex w-full flex-col items-center justify-center gap-3xs text-center';
  return <li key={item.href} className={cn(['flex w-full shrink-0 flex-col gap-3xs overflow-hidden rounded-sm px-3xs py-2xs', 'cursor-pointer', 'text-caption-md-semibold', isActive ? 'text-strong bg-opacity-violet-2 hover:bg-opacity-violet-3' : 'text-neutral hover:bg-default hover:bg-opacity-black-1'])} data-sentry-component="Menu" data-sentry-source-file="Sidebar.tsx">
      {item.href ? <Link className={innerClassName} href={item.href} onClick={item.onClick} target={item.target || '_self'}>
          <IconWrapper>
            <Icon className="size-full shrink-0" fill={isActive ? ICON_COLOR.strong : ICON_COLOR.neutral} />
          </IconWrapper>
          {item.label}
          {!!item.rightSlot && <span className="flex">{item.rightSlot}</span>}
        </Link> : <div className={innerClassName} onClick={e => {
      e.preventDefault();
      item.onClick?.();
    }}>
          <IconWrapper>
            <Icon size="sm" fill={ICON_COLOR.neutral} className="size-full shrink-0" />
          </IconWrapper>
          {item.label}
          {!!item.rightSlot && <span className="flex">{item.rightSlot}</span>}
        </div>}
    </li>;
};
const IconWrapper = (props: PropsWithChildren) => {
  return <div className="size-lg" data-sentry-component="IconWrapper" data-sentry-source-file="Sidebar.tsx">{props.children}</div>;
};